import InputSelect from "@/components/input-select/InputSelect";
import { literals } from "@/constants/literals";
import { NO_SERV_STEPS } from "@/constants/selectsLists";
import type { TemporalReceptionData } from "@/types/types.d";
import InputText from "@/components/input-text/InputText";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import ScanCode from "@/pages/capture/components/ScanCode";
import InputNumber from "@/components/input-number/InputNumber";
import { useMemo } from "react";

export type ReceptionOrderProps = {
  receptionGeneralData: TemporalReceptionData;
  isOut: boolean;
  exitFromIn: boolean;
  changeReceptionGeneralData: (
    property: string,
    value: string | undefined,
  ) => void;
  searchDataInAndCustomer?: () => void;
  lastOrderCustomerNoServ?: string;
};

const ReceptionOrder = ({
  exitFromIn,
  receptionGeneralData,
  isOut,
  changeReceptionGeneralData,
  searchDataInAndCustomer,
  lastOrderCustomerNoServ,
}: ReceptionOrderProps) => {
  const customerError = useMemo(
    () =>
      receptionGeneralData.customer && receptionGeneralData.customer.length > 6
        ? true
        : false,
    [receptionGeneralData.customer],
  );

  const changeCustomer = (value: string) => {
    if (value.length > 6) {
      changeReceptionGeneralData("customer", value);
    } else {
      changeReceptionGeneralData("customer", value);
    }
  };

  return (
    <div className="w-full px-4 md:px-14 lg:px-[calc((100%-797px)/2)] pt-4 md:flex md:flex-row md:gap-x-8 flex-wrap md:min-h-[270px]">
      <div className="w-full md:w-[calc(50%-16px)] mb-4">
        <InputSelect
          size="medium"
          label={literals.step}
          id="step"
          name="step"
          options={NO_SERV_STEPS}
          onChange={(value) => changeReceptionGeneralData("step", value)}
          value={receptionGeneralData.step}
          required
          placeholder={literals.chooseAnOption}
          disabled={exitFromIn}
        />
      </div>
      {receptionGeneralData.step !== "" ? (
        <>
          <div className="w-full md:w-[calc(50%-16px)] mb-4 flex flex-row gap-x-1">
            <div className="flex-1">
              <InputText
                placeholder={literals.orderExample}
                requiredAsterisk
                label={literals.customerOrderNoServ}
                size="medium"
                value={receptionGeneralData.customerOrderNoserv || ""}
                onChange={(value) =>
                  changeReceptionGeneralData("customerOrderNoserv", value)
                }
                readOnly={exitFromIn}
                warning={
                  isOut &&
                  lastOrderCustomerNoServ &&
                  lastOrderCustomerNoServ.length > 0 &&
                  receptionGeneralData.customerOrderNoserv !==
                    lastOrderCustomerNoServ
                    ? true
                    : false
                }
                warningMessage={literals.modificatedAlert}
                noBottomSpace={
                  receptionGeneralData.customerOrderNoserv ===
                    lastOrderCustomerNoServ ||
                  !receptionGeneralData.customerOrderNoserv
                }
              />
            </div>
            {!exitFromIn && isOut ? (
              <div className="pt-5">
                <SecondaryButton
                  colour="red"
                  size="medium-l"
                  label={literals.search}
                  icon="searchLens"
                  width="121px"
                  onClick={searchDataInAndCustomer}
                />
              </div>
            ) : null}
          </div>
          <div className="mb-4 md:w-[calc(50%-16px)]">
            <InputText
              label={literals.orderIn}
              name="orderIn"
              value={receptionGeneralData.orderIn || ""}
              onChange={(value) => changeReceptionGeneralData("orderIn", value)}
              placeholder={literals.outNumberExample}
              size="medium"
              requiredAsterisk={isOut}
              noBottomSpace
              disabled={
                isOut &&
                (!lastOrderCustomerNoServ ||
                  lastOrderCustomerNoServ.length === 0)
              }
            />
          </div>
          <div className="mb-4 md:w-[calc(50%-16px)] ">
            <InputText
              label={literals.serialInLabel}
              name="serialIn"
              value={receptionGeneralData.serialIn || ""}
              onChange={(value) =>
                changeReceptionGeneralData("serialIn", value)
              }
              placeholder={literals.outNumberExample}
              size="medium"
              requiredAsterisk={isOut}
              noBottomSpace
              disabled={
                isOut &&
                (!lastOrderCustomerNoServ ||
                  lastOrderCustomerNoServ.length === 0)
              }
            />
          </div>
          <div className="md:w-[calc(50%-16px)] ">
            <InputNumber
              label={literals.client}
              name="customer"
              value={receptionGeneralData.customer || ""}
              onChange={changeCustomer}
              placeholder={literals.customerExample}
              size="medium"
              requiredAsterisk
              error={customerError}
              errorMessage={literals.customerFormat}
              disabled={
                isOut &&
                (!lastOrderCustomerNoServ ||
                  lastOrderCustomerNoServ.length === 0)
              }
            />
          </div>
          {isOut ? (
            <div className="w-full md:w-[calc(50%-16px)] ">
              <ScanCode
                value={receptionGeneralData.orderNumber || ""}
                onChange={(value) =>
                  changeReceptionGeneralData("orderNumber", value)
                }
                codeType="sapOrder"
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ReceptionOrder;
